import { useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { LeftOutlined, RightOutlined } from "@ant-design/icons"
import dayjs from "dayjs"
import { Tooltip, Typography, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { formatDurationFields } from "utils/experience"
import { numberToCurrency } from "helpers/string"
import { useDailyDiscountCalendar } from "pagesV2/pro/Bookings/helpers/useDailyDiscountCalendar"
import styles from "./RealTimeAvailability.module.css"

const { Text } = Typography

const WEEKDAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"]
const DAYS_IN_GRID = 42 // 6 weeks × 7 days — matches the Figma calendar height

const toMonth = (date) => (date ? dayjs(date) : dayjs()).startOf("month")

/**
 * "Real-Time Availability" preview card (Figma node 4008:39370).
 *
 * Presentational calendar that marks the days guests can book with a dot. The parent works them
 * out (helpers/previewAvailability) and passes isDateAvailable(date); the calendar opens on the
 * month of firstAvailableDate.
 *
 * Discount labels from the Figma mock were originally left out here because per-date
 * pricing wasn't available client-side — `trip` now supplies day_adjustments/
 * seasonal_adjustments/pricing, so a day affected by either shows its adjusted rate under the
 * number (the dot stays "available"); hovering it breaks down original vs. adjusted price per
 * guest type (or, for a flat-rate experience, one total for the included guest count).
 * Book Now is non-interactive (this is a preview of the public page).
 */
export const RealTimeAvailability = ({
  durationType,
  durationValue,
  durationUnit,
  numberOfDays,
  isDateAvailable,
  firstAvailableDate,
  trip,
  seasonRanges
}) => {
  const { t } = useTranslation()
  const [viewMonth, setViewMonth] = useState(() => toMonth(firstAvailableDate))
  const isMultiDay = durationType === "multi_day"
  // "2 hours", "1 hour", "30 minutes"
  const durationText = durationUnit ? formatDurationFields({ duration_value: durationValue, duration_unit: durationUnit }, t) : null

  // The listing can finish loading after the first render; open on its first bookable month then.
  useEffect(() => {
    if (firstAvailableDate) setViewMonth(toMonth(firstAvailableDate))
  }, [firstAvailableDate])

  // Build a fixed 6-week grid anchored to the Sunday on/before the 1st. day()
  // returns 0 (Sun)–6 (Sat) regardless of locale, so this stays Sunday-first.
  const weeks = useMemo(() => {
    const firstOfMonth = viewMonth.startOf("month")
    const gridStart = firstOfMonth.subtract(firstOfMonth.day(), "day")
    const rows = []
    for (let week = 0; week < DAYS_IN_GRID / 7; week += 1) {
      rows.push(Array.from({ length: 7 }, (_, day) => gridStart.add(week * 7 + day, "day")))
    }
    return rows
  }, [viewMonth])

  const today = dayjs()

  const { hasPricing, priceInfoForDate, isFlatRate } = useDailyDiscountCalendar(trip, seasonRanges)

  return (
    <div className={styles.container}>
      <p className={styles.title}>
        {t("listings.tabs.preview.real_time_availability", { defaultValue: "Real-Time Availability" })}
      </p>

      {isMultiDay ? (
        <div className={styles.infoBanner}>
          <p className={styles.infoBannerTitle}>
            {t("listings.tabs.preview.multi_day_experience", {
              count: numberOfDays,
              defaultValue: `${numberOfDays} day experience`
            })}
          </p>
          <p className={styles.infoBannerSubtitle}>
            {t("listings.tabs.preview.multi_day_select_hint", {
              defaultValue: "Select a start date - the full experience length will be applied automatically"
            })}
          </p>
        </div>
      ) : (
        durationText && (
          <div className={styles.infoBanner}>
            <p className={styles.infoBannerTitle}>
              {t("listings.tabs.preview.duration_label", { duration: durationText })}
            </p>
          </div>
        )
      )}

      <div className={styles.card}>
        <p className={styles.selectDate}>
          {t("listings.tabs.preview.select_date", { defaultValue: "Select Date" })}
        </p>
        <div className={styles.legend}>
          <span className={styles.legendDot} />
          <span className={styles.legendLabel}>
            {t("listings.tabs.preview.available_days", { defaultValue: "Available days" })}
          </span>
        </div>
        <div className={styles.calendar}>
          <div className={styles.monthHeader}>
            <button
              type="button"
              className={styles.navBtn}
              onClick={() => setViewMonth((month) => month.subtract(1, "month"))}
              aria-label={t("global.previous", { defaultValue: "Previous" })}
            >
              <LeftOutlined />
            </button>
            <span className={styles.monthLabel}>{viewMonth.format("MMMM YYYY")}</span>
            <button
              type="button"
              className={styles.navBtn}
              onClick={() => setViewMonth((month) => month.add(1, "month"))}
              aria-label={t("global.next", { defaultValue: "Next" })}
            >
              <RightOutlined />
            </button>
          </div>

          <div className={styles.weekRow}>
            {WEEKDAY_LABELS.map((label, index) => (
              <span key={index} className={styles.weekday}>
                {label}
              </span>
            ))}
          </div>

          {weeks.map((week) => (
            <div key={week[0].format("YYYY-MM-DD")} className={styles.dayRow}>
              {week.map((date) => {
                const inMonth = date.month() === viewMonth.month()
                const isAvailable = inMonth && isDateAvailable(date)
                const isToday = date.isSame(today, "day")
                const dayClasses = [
                  styles.dayNum,
                  inMonth ? "" : styles.dayMuted,
                  isToday ? styles.dayToday : ""
                ]
                  .filter(Boolean)
                  .join(" ")

                const info = hasPricing ? priceInfoForDate(date) : null
                const cell = (
                  <div key={date.format("YYYY-MM-DD")} className={styles.dayCell}>
                    {isAvailable && <span className={styles.dayDot} />}
                    <div className={styles.dayCellContent}>
                      <span className={dayClasses}>{date.date()}</span>
                      {info?.hasDiscount && (
                        <span className={styles.dayPriceDiscounted}>{numberToCurrency(info.headline)}</span>
                      )}
                    </div>
                  </div>
                )

                if (!info?.hasDiscount) return cell

                return (
                  <Tooltip
                    key={date.format("YYYY-MM-DD")}
                    placement="top"
                    title={
                      <Flex vertical gap={4} className={styles.tooltipContent}>
                        <Text strong className={styles.tooltipHeading}>
                          {isFlatRate
                            ? t("listings.tabs.preview.pricing_preview.min_price", { defaultValue: "Min. price:" })
                            : t("listings.tabs.preview.pricing_preview.min_price_per_person", {
                                defaultValue: "Min. price per person:"
                              })}
                        </Text>
                        {info.rows.map((row) => (
                          <Flex key={row.key} justify="space-between" gap={12}>
                            <span className={styles.tooltipLabel}>{row.label}</span>
                            <span>
                              <Text delete className={styles.tooltipOriginal}>
                                {numberToCurrency(row.original)}
                              </Text>
                              <Text strong className={styles.tooltipAdjusted}>
                                {numberToCurrency(row.adjusted)}
                              </Text>
                            </span>
                          </Flex>
                        ))}
                      </Flex>
                    }
                  >
                    {cell}
                  </Tooltip>
                )
              })}
            </div>
          ))}
        </div>
      </div>

      <div className={styles.footer}>
        {/* Non-interactive in preview — keeps the public look without booking. */}
        <div className={styles.bookBtn} role="presentation">
          {t("listings.tabs.preview.book_now", { defaultValue: "Book Now" })}
        </div>
        <p className={styles.disclaimer}>
          {t("listings.tabs.preview.checkout_info", {
            defaultValue: "Payment information will be collected at checkout."
          })}
        </p>
      </div>
    </div>
  )
}

RealTimeAvailability.propTypes = {
  durationType: PropTypes.string,
  durationValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  durationUnit: PropTypes.string,
  numberOfDays: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isDateAvailable: PropTypes.func,
  firstAvailableDate: PropTypes.string,
  trip: PropTypes.object,
  seasonRanges: PropTypes.arrayOf(PropTypes.shape({ start: PropTypes.string, end: PropTypes.string }))
}

RealTimeAvailability.defaultProps = {
  durationType: undefined,
  durationValue: undefined,
  durationUnit: undefined,
  numberOfDays: undefined,
  isDateAvailable: () => false,
  firstAvailableDate: null,
  trip: null,
  seasonRanges: []
}
